import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Handshake, Calendar, Users, Building2, CheckCircle, Clock, Info, FileText, BarChart3, ExternalLink, CheckSquare, FileImage, Link as LinkIcon, Copy, Check, X, Pencil, Trash2, Plus, Upload, Download, Eye, AlertCircle } from 'lucide-react';
import { Dialog } from '@headlessui/react';
import { Collab, collabsRef, timelineItemsRef, collabDocumentsRef, collabStatsRef, createDocument, updateDocument, deleteDocument, storage, ref, uploadBytesResumable, getDownloadURL, collection, query, where, getDocs, doc, setDoc, serverTimestamp, updateDoc } from '../lib/firebase';
import { getDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useTheme } from '../contexts/ThemeContext';

export default function CollaborationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [campaign, setCampaign] = useState<Collab | null>(null);
  const [timelineItems, setTimelineItems] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [stats, setStats] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeTab, setActiveTab] = useState('timeline');
  const [copiedLink, setCopiedLink] = useState(false);
  const [isTimelineModalOpen, setIsTimelineModalOpen] = useState(false);
  const [isDocumentModalOpen, setIsDocumentModalOpen] = useState(false);
  const [isStatsModalOpen, setIsStatsModalOpen] = useState(false);
  const [selectedInfluencerId, setSelectedInfluencerId] = useState<string>('');
  const [selectedInfluencerName, setSelectedInfluencerName] = useState<string>('');
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<any | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploading, setIsUploading] = useState(false);
  
  const [timelineForm, setTimelineForm] = useState({
    stepName: '',
    deadline: '',
    status: 'todo',
    order: 0,
    influencerId: '',
    influencerName: ''
  });
  
  const [documentForm, setDocumentForm] = useState({
    name: '',
    type: 'brief',
    visibleForBrand: false,
    influencerId: '',
    influencerName: ''
  });
  
  const [statsForm, setStatsForm] = useState({
    creator: '',
    views: 0,
    likes: 0,
    engagementRate: 0,
    postUrl: '',
    postThumb: '',
    influencerId: '',
    influencerName: ''
  });

  useEffect(() => {
    if (id) {
      loadCampaignData();
    }
  }, [id]);

  const loadCampaignData = async () => {
    try {
      setIsLoading(true);
      setError(null);

      // Récupérer les détails de la collaboration
      const campaignDoc = await getDoc(doc(db, 'collabs', id!));
      if (!campaignDoc.exists()) {
        setError('Cette collaboration n\'existe pas');
        setIsLoading(false);
        return;
      }
      
      const campaignData = { id: campaignDoc.id, ...campaignDoc.data() } as Collab;
      setCampaign(campaignData);
      
      // Récupérer les éléments de timeline spécifiques à cette collaboration
      const timelineQuery = query(
        collection(db, 'timelineItems'),
        where('collabId', '==', id)
      );
      
      const timelineSnapshot = await getDocs(timelineQuery);
      const timelineData = timelineSnapshot.docs
        .map(doc => ({ id: doc.id, ...doc.data() }))
        .sort((a, b) => a.order - b.order);
      
      setTimelineItems(timelineData);
      
      // Récupérer les documents spécifiques à cette collaboration
      const documentsQuery = query(
        collection(db, 'collabDocuments'),
        where('collabId', '==', id)
      );
      
      const documentsSnapshot = await getDocs(documentsQuery);
      const documentsData = documentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setDocuments(documentsData);
      
      // Récupérer les statistiques spécifiques à cette collaboration
      const statsQuery = query(
        collection(db, 'collabStats'),
        where('collabId', '==', id)
      );
      
      const statsSnapshot = await getDocs(statsQuery);
      const statsData = statsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setStats(statsData);
      
    } catch (error) {
      console.error('Error loading campaign data:', error);
      setError('Une erreur est survenue lors du chargement des données');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTimelineStatusChange = async (itemId: string, newStatus: string) => {
    try {
      setIsUpdating(true);
      
      // Trouver l'élément dans le state local
      const updatedTimelineItems = timelineItems.map(item => {
        if (item.id === itemId) {
          return { ...item, status: newStatus };
        }
        return item;
      });
      
      // Mettre à jour l'état local immédiatement pour une meilleure UX
      setTimelineItems(updatedTimelineItems);
      
      // Mettre à jour dans Firestore
      const timelineItemRef = doc(db, 'timelineItems', itemId);
      await updateDoc(timelineItemRef, {
        status: newStatus,
        updatedAt: serverTimestamp()
      });
      
      // Pas besoin de recharger tous les éléments puisque nous avons déjà mis à jour l'état local
    } catch (error) {
      console.error('Error updating timeline status:', error);
      setError('Erreur lors de la mise à jour du statut');
      // En cas d'erreur, recharger les données pour s'assurer de la cohérence
      await loadCampaignData();
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteTimelineItem = async (itemId: string) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cette étape ?')) {
      return;
    }
    
    try {
      await deleteDocument(timelineItemsRef, itemId);
      setTimelineItems(prev => prev.filter(item => item.id !== itemId));
    } catch (error) {
      console.error('Error deleting timeline item:', error);
      setError('Erreur lors de la suppression de l\'étape');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleAddTimelineItem = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Créer l'objet de base sans les champs d'influenceur
      let timelineData: any = {
        collabId: id,
        stepName: timelineForm.stepName,
        deadline: timelineForm.deadline ? new Date(timelineForm.deadline) : null,
        status: timelineForm.status,
        order: timelineItems.length,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Ajouter les champs d'influenceur seulement si un influenceur est sélectionné
      if (selectedInfluencerId && selectedInfluencerId.trim() !== '') {
        timelineData.influencerId = selectedInfluencerId;
        timelineData.influencerName = selectedInfluencerName;
      }
      
      await createDocument(timelineItemsRef, timelineData);
      
      await loadCampaignData();
      setIsTimelineModalOpen(false);
      resetTimelineForm();
      setSelectedInfluencerId('');
      setSelectedInfluencerName('');
    } catch (error) {
      console.error('Error adding timeline item:', error);
    }
  };

  const handleAddDocument = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!selectedFile) {
      return;
    }
    
    try {
      setIsUploading(true);
      
      // Upload file to storage
      const storageRef = ref(storage, `documents/${id}/${Date.now()}_${selectedFile.name}`);
      const uploadTask = uploadBytesResumable(storageRef, selectedFile);
      
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
          setUploadProgress(progress);
        },
        (error) => {
          console.error('Error uploading file:', error);
          setIsUploading(false);
        },
        async () => {
          // Get download URL
          const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
          
          // Créer l'objet de base sans les champs d'influenceur
          let documentData: any = {
            collabId: id,
            name: documentForm.name,
            type: documentForm.type,
            fileUrl: downloadURL,
            fileName: selectedFile.name,
            fileSize: selectedFile.size,
            visibleForBrand: documentForm.visibleForBrand,
            createdAt: new Date(),
            updatedAt: new Date()
          };
          
          // Ajouter les champs d'influenceur seulement si un influenceur est sélectionné
          if (selectedInfluencerId && selectedInfluencerId.trim() !== '') {
            documentData.influencerId = selectedInfluencerId;
            documentData.influencerName = selectedInfluencerName;
          }
          
          // Save document metadata to Firestore
          await createDocument(collabDocumentsRef, documentData);
          
          await loadCampaignData();
          setIsDocumentModalOpen(false);
          resetDocumentForm();
          setSelectedInfluencerId('');
          setSelectedInfluencerName('');
          setIsUploading(false);
        }
      );
    } catch (error) {
      console.error('Error adding document:', error);
      setIsUploading(false);
    }
  };

  const handleAddStats = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      // Créer l'objet de base sans les champs d'influenceur
      let statsData: any = {
        collabId: id,
        creator: statsForm.creator,
        views: parseInt(String(statsForm.views)),
        likes: parseInt(String(statsForm.likes)),
        engagementRate: parseFloat(String(statsForm.engagementRate)),
        postUrl: statsForm.postUrl,
        postThumb: statsForm.postThumb,
        createdAt: new Date(),
        updatedAt: new Date()
      };
      
      // Ajouter les champs d'influenceur seulement si un influenceur est sélectionné
      if (selectedInfluencerId && selectedInfluencerId.trim() !== '') {
        statsData.influencerId = selectedInfluencerId;
        statsData.influencerName = selectedInfluencerName;
      }
      
      await createDocument(collabStatsRef, statsData);
      
      await loadCampaignData();
      setIsStatsModalOpen(false);
      resetStatsForm();
      setSelectedInfluencerId('');
      setSelectedInfluencerName('');
    } catch (error) {
      console.error('Error adding stats:', error);
    }
  };

  const handleDeleteItem = async (type: string, itemId: string) => {
    try {
      if (type === 'timeline') {
        await deleteDocument(timelineItemsRef, itemId);
      } else if (type === 'document') {
        await deleteDocument(collabDocumentsRef, itemId);
      } else if (type === 'stats') {
        await deleteDocument(collabStatsRef, itemId);
      }
      
      await loadCampaignData();
      setIsDeleteModalOpen(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error deleting item:', error);
    }
  };

  const handleGeneratePublicLink = async () => {
    try {
      if (!campaign) return;
      
      const linkToken = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
      
      await updateDocument(collabsRef, campaign.id!, {
        linkToken,
        linkActive: true,
        updatedAt: new Date()
      });
      
      await loadCampaignData();
      setIsLinkModalOpen(true);
    } catch (error) {
      console.error('Error generating public link:', error);
    }
  };

  const copyPublicLink = async () => {
    if (!campaign?.linkToken) return;
    
    const publicUrl = `${window.location.origin}/suivi/${campaign.linkToken}`;
    await navigator.clipboard.writeText(publicUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const toggleLinkStatus = async () => {
    try {
      if (!campaign) return;
      
      await updateDocument(collabsRef, campaign.id!, {
        linkActive: !campaign.linkActive,
        updatedAt: new Date()
      });
      
      await loadCampaignData();
    } catch (error) {
      console.error('Error toggling link status:', error);
    }
  };

  const resetTimelineForm = () => {
    setTimelineForm({
      stepName: '',
      deadline: '',
      status: 'todo',
      order: 0,
      influencerId: '',
      influencerName: ''
    });
  };

  const resetDocumentForm = () => {
    setDocumentForm({
      name: '',
      type: 'brief',
      visibleForBrand: false,
      influencerId: '',
      influencerName: ''
    });
    setSelectedFile(null);
    setUploadProgress(0);
  };

  const resetStatsForm = () => {
    setStatsForm({
      creator: '',
      views: 0,
      likes: 0,
      engagementRate: 0,
      postUrl: '',
      postThumb: '',
      influencerId: '',
      influencerName: ''
    });
  };

  const formatDate = (date: Date | { seconds: number } | string | undefined) => {
    if (!date) return 'Date non définie';
    
    if (typeof date === 'string') {
      return new Date(date).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    if (typeof date === 'object' && date !== null && 'seconds' in date) {
      return new Date(date.seconds * 1000).toLocaleDateString('fr-FR', {
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      });
    }
    
    return new Date(date).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount: string | number) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(num)) return '0 €';
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR'
    }).format(num);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'done':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'in_progress':
        return <Clock className="w-5 h-5 text-yellow-500" />;
      case 'late':
        return <Info className="w-5 h-5 text-red-500" />;
      default:
        return <CheckSquare className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'done':
        return 'Terminé';
      case 'in_progress':
        return 'En cours';
      case 'late':
        return 'En retard';
      default:
        return 'À faire';
    }
  };

  const getStatusClass = (status: string) => {
    switch (status) {
      case 'done':
        return 'bg-green-500/20 text-green-300 border-green-500/20';
      case 'in_progress':
        return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/20';
      case 'late':
        return 'bg-red-500/20 text-red-300 border-red-500/20';
      default:
        return 'bg-gray-500/20 text-gray-300 border-gray-500/20';
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent-blue"></div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-4">
          <Info className="w-8 h-8 text-red-500" />
        </div>
        <h3 className="text-lg font-medium text-white mb-2">Erreur</h3>
        <p className="text-gray-400">{error || 'Impossible de charger les détails de la collaboration'}</p>
        <button
          onClick={() => navigate('/app/collaborations')}
          className="mt-4 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90"
        >
          Retour aux collaborations
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-accent-purple to-accent-blue flex items-center justify-center">
            <Handshake className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className={`text-3xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              {campaign.name}
            </h1>
            <div className="flex items-center gap-2">
              <Building2 className={`w-4 h-4 ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`} />
              <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                {campaign.brand}
              </p>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate(`/app/collaborations/new?id=${id}`)}
            className={`flex items-center gap-2 px-4 py-2 ${
              theme === 'dark'
                ? 'bg-dark-200 text-gray-300 hover:bg-dark-300'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            } rounded-lg transition-colors`}
          >
            <Pencil className="w-4 h-4" />
            Modifier
          </button>
          <button
            onClick={() => campaign.linkToken ? setIsLinkModalOpen(true) : handleGeneratePublicLink()}
            className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90"
          >
            <LinkIcon className="w-4 h-4" />
            {campaign.linkToken ? 'Gérer le lien' : 'Générer un lien'}
          </button>
        </div>
      </div>

      {/* Info cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className={`${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl p-6 border`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Date</h2>
            <Calendar className={`w-5 h-5 ${theme === 'dark' ? 'text-accent-blue' : 'text-indigo-600'}`} />
          </div>
          <p className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {formatDate(campaign.date)}
          </p>
        </div>
        
        <div className={`${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl p-6 border`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Chef de projet</h2>
            <Users className={`w-5 h-5 ${theme === 'dark' ? 'text-accent-blue' : 'text-indigo-600'}`} />
          </div>
          <p className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {campaign.manager}
          </p>
        </div>
        
        <div className={`${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl p-6 border`}>
          <div className="flex items-center justify-between mb-4">
            <h2 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>Budget</h2>
            <BarChart3 className={`w-5 h-5 ${theme === 'dark' ? 'text-accent-blue' : 'text-indigo-600'}`} />
          </div>
          <p className={`text-xl font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {formatCurrency(campaign.budget || campaign.influencers.reduce((sum, inf) => sum + parseFloat(inf.rate || '0'), 0))}
          </p>
        </div>
      </div>

      {/* Tabs */}
      <div className={`${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl border`}>
        <div className={`border-b ${theme === 'dark' ? 'border-dark-200' : 'border-gray-200'}`}>
          <nav className="flex -mb-px">
            {[
              { id: 'timeline', label: 'Timeline', icon: Calendar },
              { id: 'influencers', label: 'Influenceurs', icon: Users },
              { id: 'documents', label: 'Documents', icon: FileText },
              { id: 'stats', label: 'Statistiques', icon: BarChart3 }
            ].map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center px-6 py-4 font-medium text-sm border-b-2 ${
                    activeTab === tab.id
                      ? `${theme === 'dark' ? 'border-accent-blue text-accent-blue' : 'border-indigo-600 text-indigo-600'}`
                      : `border-transparent ${theme === 'dark' ? 'text-gray-400 hover:text-gray-300 hover:border-gray-700' : 'text-gray-500 hover:text-gray-700 hover:border-gray-300'}`
                  }`}
                >
                  <Icon className="w-5 h-5 mr-2" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Timeline Tab */}
          {activeTab === 'timeline' && (
            <div className="space-y-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Avancement de la campagne
                </h2>
                <button
                  onClick={() => {
                    resetTimelineForm();
                    setIsTimelineModalOpen(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter une étape
                </button>
              </div>
              
              {timelineItems.length === 0 ? (
                <div className={`text-center py-12 ${theme === 'dark' ? 'bg-dark-100' : 'bg-gray-50'} rounded-lg`}>
                  <Calendar className={`w-12 h-12 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'} mx-auto mb-4`} />
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                    Aucune étape définie pour cette campagne
                  </p>
                </div>
              ) : (
                <div className="space-y-6">
                  {timelineItems.map((item, index) => (
                    <div key={item.id} className="relative">
                      {index < timelineItems.length - 1 && (
                        <div className={`absolute top-8 left-4 bottom-0 w-0.5 ${theme === 'dark' ? 'bg-dark-200' : 'bg-gray-200'}`}></div>
                      )}
                      
                      <div className="flex items-start gap-4">
                        <div 
                          className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                            item.status === 'done' ? 'bg-green-500/20 text-green-300 cursor-pointer' :
                            item.status === 'in_progress' ? 'bg-yellow-500/20 text-yellow-300 cursor-pointer' :
                            item.status === 'late' ? 'bg-red-500/20 text-red-300 cursor-pointer' :
                            'bg-gray-500/20 text-gray-300 cursor-pointer'
                          }`}
                          onClick={() => {
                            // Rotation des statuts: todo -> in_progress -> done -> todo
                            const nextStatus = 
                              item.status === 'todo' ? 'in_progress' :
                              item.status === 'in_progress' ? 'done' :
                              'todo';
                            handleTimelineStatusChange(item.id, nextStatus);
                          }}
                          title="Cliquer pour changer le statut"
                        >
                          {item.status === 'done' ? (
                            <CheckCircle className="w-5 h-5" />
                          ) : item.status === 'in_progress' ? (
                            <Clock className="w-5 h-5" />
                          ) : item.status === 'late' ? (
                            <Info className="w-5 h-5" />
                          ) : (
                            <CheckSquare className="w-5 h-5" />
                          )}
                        </div>
                        
                        <div className="flex-1 bg-white border border-black rounded-lg p-4">
                          <div className="flex items-center justify-between mb-2">
                            <h3 className="font-medium text-gray-900">{item.stepName}</h3>
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => {
                                  // Rotation des statuts: todo -> in_progress -> done -> todo
                                  const nextStatus = 
                                    item.status === 'todo' ? 'in_progress' :
                                    item.status === 'in_progress' ? 'done' :
                                    'todo';
                                  handleTimelineStatusChange(item.id, nextStatus);
                                }}
                                className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-medium border ${
                                item.status === 'done' ? 'bg-green-100 text-green-700 border-green-600' :
                                item.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700 border-yellow-600' :
                                item.status === 'late' ? 'bg-red-100 text-red-700 border-red-600' :
                                'bg-blue-100 text-blue-700 border-blue-600'
                              } cursor-pointer hover:opacity-80 transition-opacity`}>
                                {item.status === 'done' ? <CheckCircle className="w-3.5 h-3.5" /> :
                                 item.status === 'in_progress' ? <Clock className="w-3.5 h-3.5" /> :
                                 item.status === 'late' ? <AlertCircle className="w-3.5 h-3.5" /> :
                                 <CheckSquare className="w-3.5 h-3.5" />}
                                {item.status === 'done' ? 'Terminé' :
                                 item.status === 'in_progress' ? 'En cours' :
                                 item.status === 'late' ? 'En retard' :
                                 'À faire'}
                              </button>
                              <button
                                onClick={() => handleDeleteTimelineItem(item.id!)}
                                className="p-1 text-red-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Supprimer cette étape"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                          
                          {item.deadline && (
                            <div className="flex items-center gap-2 text-sm text-gray-600">
                              <Calendar className="w-4 h-4" />
                              <span>Échéance: {formatDate(item.deadline)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Influencers Tab */}
          {activeTab === 'influencers' && (
            <div>
              <h2 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
                Influenceurs sélectionnés
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {campaign.influencers && campaign.influencers.length > 0 ? (
                  campaign.influencers.map((influencer, index) => (
                    <div 
                      key={index} 
                      className={`${theme === 'dark' ? 'bg-dark-100' : 'bg-white'} rounded-lg border ${theme === 'dark' ? 'border-dark-200' : 'border-gray-200'} p-4`}
                    >
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full ${theme === 'dark' ? 'bg-accent-blue/10' : 'bg-indigo-100'} flex items-center justify-center`}>
                            <Users className={`w-5 h-5 ${theme === 'dark' ? 'text-accent-blue' : 'text-indigo-600'}`} />
                          </div>
                          <div className="flex-1">
                            <h3 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {influencer.fullName}
                            </h3>
                          </div>
                          <div className="text-right">
                            <span className={`text-lg font-semibold ${theme === 'dark' ? 'text-accent-blue' : 'text-indigo-600'}`}>
                              {formatCurrency(influencer.rate || '0')}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {influencer.deliverables && influencer.deliverables.length > 0 && (
                        <div className="mt-4">
                          <h4 className={`text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'} mb-2`}>
                            Livrables:
                          </h4>
                          <div className={`space-y-2 divide-y ${theme === 'dark' ? 'divide-dark-200' : 'divide-gray-100'}`}>
                            {influencer.deliverables.map((deliverable, idx) => (
                              <div key={idx} className="flex items-center justify-between text-sm py-2">
                                <div className={theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}>
                                  <span className={theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}>
                                    {deliverable.type}
                                  </span>
                                  {deliverable.description && (
                                    <span> - {deliverable.description}</span>
                                  )}
                                </div>
                                <span className={`font-medium ${theme === 'dark' ? 'text-accent-blue' : 'text-indigo-600'}`}>
                                  {formatCurrency(deliverable.price || '0')}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <div className="mt-4 pt-4 border-t border-gray-100">
                        <div className="flex items-center justify-between">
                          <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            Facture créateur
                          </span>
                          <div className="flex items-center gap-2">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                              influencer.creator_invoice_sent
                                ? 'bg-green-500/20 text-green-300 border border-green-500/20'
                                : 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/20'
                            }`}>
                              {influencer.creator_invoice_sent ? 'Reçue' : 'En attente'}
                            </span>
                            {influencer.paidPercentage !== undefined && (
                              <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                                ({influencer.paidPercentage}%)
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className={`col-span-2 text-center py-12 ${theme === 'dark' ? 'bg-dark-100' : 'bg-gray-50'} rounded-lg`}>
                    <Users className={`w-12 h-12 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'} mx-auto mb-4`} />
                    <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                      Aucun influenceur associé à cette campagne
                    </p>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Documents Tab */}
          {activeTab === 'documents' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Documents et preview
                </h2>
                <button
                  onClick={() => {
                    resetDocumentForm();
                    setIsDocumentModalOpen(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter un document
                </button>
              </div>
              
              {documents.length === 0 ? (
                <div className={`text-center py-12 ${theme === 'dark' ? 'bg-dark-100' : 'bg-gray-50'} rounded-lg`}>
                  <FileImage className={`w-12 h-12 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'} mx-auto mb-4`} />
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                    Aucun document ou preview disponible pour cette campagne
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {documents.map((document) => (
                    <div 
                      key={document.id} 
                      className={`${theme === 'dark' ? 'bg-dark-100' : 'bg-white'} rounded-lg border ${theme === 'dark' ? 'border-dark-200' : 'border-gray-200'} p-4`}
                    >
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-full ${theme === 'dark' ? 'bg-accent-blue/10' : 'bg-indigo-100'} flex items-center justify-center`}>
                          <FileText className={`w-5 h-5 ${theme === 'dark' ? 'text-accent-blue' : 'text-indigo-600'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h3 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'} truncate`}>
                            {document.name}
                          </h3>
                          <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                            {document.type}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => {
                              setSelectedItem({ ...document, type: 'document' });
                              setIsDeleteModalOpen(true);
                            }}
                            className={`p-1 ${theme === 'dark' ? 'text-red-500 hover:text-red-400' : 'text-red-600 hover:text-red-500'} rounded-lg`}
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex items-center justify-between text-sm">
                        <div className="flex items-center gap-2">
                          <span className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                            {formatFileSize(document.fileSize)}
                          </span>
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            document.visibleForBrand
                              ? 'bg-green-500/20 text-green-300 border border-green-500/20'
                              : 'bg-gray-500/20 text-gray-400 border border-gray-500/20'
                          }`}>
                            {document.visibleForBrand ? 'Visible pour la marque' : 'Interne'}
                          </span>
                        </div>
                        <a
                          href={document.fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className={`flex items-center gap-1 ${theme === 'dark' ? 'text-accent-blue hover:text-accent-blue/80' : 'text-indigo-600 hover:text-indigo-800'}`}
                        >
                          <Download className="w-4 h-4" />
                          <span>Télécharger</span>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Stats Tab */}
          {activeTab === 'stats' && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                  Statistiques de performance
                </h2>
                <button
                  onClick={() => {
                    resetStatsForm();
                    setIsStatsModalOpen(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-accent-blue text-white rounded-lg hover:bg-accent-blue/90"
                >
                  <Plus className="w-4 h-4" />
                  Ajouter des statistiques
                </button>
              </div>
              
              {stats.length === 0 ? (
                <div className={`text-center py-12 ${theme === 'dark' ? 'bg-dark-100' : 'bg-gray-50'} rounded-lg`}>
                  <BarChart3 className={`w-12 h-12 ${theme === 'dark' ? 'text-gray-600' : 'text-gray-300'} mx-auto mb-4`} />
                  <p className={theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}>
                    Aucune statistique disponible pour cette campagne
                  </p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {stats.map((stat) => (
                    <div 
                      key={stat.id} 
                      className={`${theme === 'dark' ? 'bg-dark-100' : 'bg-white'} rounded-lg border ${theme === 'dark' ? 'border-dark-200' : 'border-gray-200'} p-4`}
                    >
                      {stat.postThumb && (
                        <div className="mb-4 aspect-w-16 aspect-h-9 rounded-lg overflow-hidden">
                          <img
                            src={stat.postThumb}
                            alt="Aperçu du post"
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <h3 className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                            {stat.creator}
                          </h3>
                          <button
                            onClick={() => {
                              setSelectedItem({ ...stat, type: 'stats' });
                              setIsDeleteModalOpen(true);
                            }}
                            className={`p-1 ${theme === 'dark' ? 'text-red-500 hover:text-red-400' : 'text-red-600 hover:text-red-500'} rounded-lg`}
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                        
                        <div className="grid grid-cols-3 gap-3">
                          <div className={`${theme === 'dark' ? 'bg-dark-200' : 'bg-gray-50'} rounded-lg p-3 text-center`}>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
                              Vues
                            </p>
                            <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {typeof stat.views === 'number' ? stat.views.toLocaleString() : '0'}
                            </p>
                          </div>
                          <div className={`${theme === 'dark' ? 'bg-dark-200' : 'bg-gray-50'} rounded-lg p-3 text-center`}>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
                              Likes
                            </p>
                            <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {typeof stat.likes === 'number' ? stat.likes.toLocaleString() : '0'}
                            </p>
                          </div>
                          <div className={`${theme === 'dark' ? 'bg-dark-200' : 'bg-gray-50'} rounded-lg p-3 text-center`}>
                            <p className={`text-xs ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'} mb-1`}>
                              Engagement
                            </p>
                            <p className={`text-lg font-semibold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                              {stat.engagementRate || '0'}%
                            </p>
                          </div>
                        </div>
                        
                        {stat.postUrl && (
                          <a
                            href={stat.postUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className={`flex items-center justify-center gap-2 w-full py-2 px-4 ${
                              theme === 'dark'
                                ? 'bg-dark-200 text-accent-blue hover:bg-dark-300'
                                : 'bg-indigo-50 text-indigo-600 hover:bg-indigo-100'
                            } rounded-lg transition-colors text-sm font-medium`}
                          >
                            <ExternalLink className="w-4 h-4" />
                            <span>Voir le post</span>
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Timeline Modal */}
      <Dialog
        open={isTimelineModalOpen}
        onClose={() => setIsTimelineModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className={`fixed inset-0 ${theme === 'dark' ? 'bg-black' : 'bg-gray-500'} bg-opacity-30`} />
          <div className={`relative ${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl max-w-md w-full mx-4 p-6 border`}>
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsTimelineModalOpen(false)}
                className={theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-600'}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
              Ajouter une étape
            </h2>

            <form onSubmit={handleAddTimelineItem} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                  Nom de l'étape *
                </label>
                <input
                  type="text"
                  value={timelineForm.stepName}
                  onChange={(e) => setTimelineForm({ ...timelineForm, stepName: e.target.value })}
                  className="w-full border-2 border-black"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Influenceur concerné (optionnel)
                </label>
                <select
                  value={selectedInfluencerId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedInfluencerId(id);
                    if (id) {
                      const inf = campaign?.influencers.find(i => i.id === id);
                      setSelectedInfluencerName(inf?.fullName || '');
                    } else {
                      setSelectedInfluencerName('');
                    }
                  }}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900"
                >
                  <option value="">Tous les influenceurs</option>
                  {campaign?.influencers.map((influencer) => (
                    <option key={influencer.id} value={influencer.id}>
                      {influencer.fullName}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Si cette étape concerne un influenceur spécifique, sélectionnez-le ici.
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                  Date d'échéance (optionnelle)
                </label>
                <input
                  type="date"
                  value={timelineForm.deadline}
                  onChange={(e) => setTimelineForm({ ...timelineForm, deadline: e.target.value })}
                  className="w-full border-2 border-black"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                  Statut
                </label>
                <select
                  value={timelineForm.status}
                  onChange={(e) => setTimelineForm({ ...timelineForm, status: e.target.value })}
                  className="w-full border-2 border-black"
                >
                  <option value="todo">À faire</option>
                  <option value="in_progress">En cours</option>
                  <option value="done">Terminé</option>
                  <option value="late">En retard</option>
                </select>
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsTimelineModalOpen(false)}
                  className={`px-4 py-2 text-sm font-medium ${
                    theme === 'dark'
                      ? 'text-gray-300 bg-dark-200 border-dark-300 hover:bg-dark-300'
                      : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                  } border-2 border-black rounded-lg transition-colors`}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-accent-blue border-2 border-black rounded-lg hover:bg-accent-blue/90"
                >
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* Document Modal */}
      <Dialog
        open={isDocumentModalOpen}
        onClose={() => setIsDocumentModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className={`fixed inset-0 ${theme === 'dark' ? 'bg-black' : 'bg-gray-500'} bg-opacity-30`} />
          <div className={`relative ${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl max-w-md w-full mx-4 p-6 border`}>
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsDocumentModalOpen(false)}
                className={theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-600'}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
              Ajouter un document
            </h2>

            <form onSubmit={handleAddDocument} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                  Nom du document *
                </label>
                <input
                  type="text"
                  value={documentForm.name}
                  onChange={(e) => setDocumentForm({ ...documentForm, name: e.target.value })}
                  className="w-full border-2 border-black"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Influenceur concerné (optionnel)
                </label>
                <select
                  value={selectedInfluencerId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedInfluencerId(id);
                    if (id) {
                      const inf = campaign?.influencers.find(i => i.id === id);
                      setSelectedInfluencerName(inf?.fullName || '');
                    } else {
                      setSelectedInfluencerName('');
                    }
                  }}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900"
                >
                  <option value="">Tous les influenceurs</option>
                  {campaign?.influencers.map((influencer) => (
                    <option key={influencer.id} value={influencer.id}>
                      {influencer.fullName}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Si ce document concerne un influenceur spécifique, sélectionnez-le ici.
                </p>
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                  Type
                </label>
                <select
                  value={documentForm.type}
                  onChange={(e) => setDocumentForm({ ...documentForm, type: e.target.value })}
                  className="w-full border-2 border-black"
                >
                  <option value="brief">Brief</option>
                  <option value="contract">Contrat</option>
                  <option value="preview">Preview</option>
                  <option value="other">Autre</option>
                </select>
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                  Fichier
                </label>
                <div 
                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer ${
                    theme === 'dark' 
                      ? 'border-dark-200 hover:border-accent-blue/50 bg-dark-100'
                      : 'border-gray-300 hover:border-indigo-500/50 bg-gray-50'
                  }`}
                  onClick={() => document.getElementById('file-upload')?.click()}
                >
                  <input
                    id="file-upload"
                    type="file"
                    className="hidden"
                    onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                  />
                  <div className="flex flex-col items-center gap-2">
                    <Upload className={`w-8 h-8 ${theme === 'dark' ? 'text-accent-blue' : 'text-indigo-600'}`} />
                    {selectedFile ? (
                      <div className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>
                        <p className="font-medium">{selectedFile.name}</p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          {formatFileSize(selectedFile.size)}
                        </p>
                      </div>
                    ) : (
                      <>
                        <p className={`font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                          Cliquez pour sélectionner un fichier
                        </p>
                        <p className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                          PDF, Word, Excel, Images, etc.
                        </p>
                      </>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="visibleForBrand"
                  checked={documentForm.visibleForBrand}
                  onChange={(e) => setDocumentForm({ ...documentForm, visibleForBrand: e.target.checked })}
                  className={`h-4 w-4 rounded ${
                    theme === 'dark'
                      ? 'bg-dark-200 border-dark-300 text-accent-blue focus:ring-accent-blue'
                      : 'bg-white border-gray-300 text-indigo-600 focus:ring-indigo-500'
                  }`}
                />
                <label 
                  htmlFor="visibleForBrand" 
                  className={`text-sm ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'}`}
                >
                  Visible pour la marque
                </label>
              </div>

              {isUploading && (
                <div className="mt-4">
                  <div className="flex items-center justify-between mb-1">
                    <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      Téléchargement en cours...
                    </span>
                    <span className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                      {uploadProgress.toFixed(0)}%
                    </span>
                  </div>
                  <div className={`w-full ${theme === 'dark' ? 'bg-dark-200' : 'bg-gray-200'} rounded-full h-2`}>
                    <div
                      className="bg-accent-blue h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress}%` }}
                    />
                  </div>
                </div>
              )}

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsDocumentModalOpen(false)}
                  className={`px-4 py-2 text-sm font-medium ${
                    theme === 'dark'
                      ? 'text-gray-300 bg-dark-200 border-dark-300 hover:bg-dark-300'
                      : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                  } border-2 border-black rounded-lg transition-colors`}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={!selectedFile || isUploading}
                  className="px-4 py-2 text-sm font-medium text-white bg-accent-blue border-2 border-black rounded-lg hover:bg-accent-blue/90 disabled:opacity-50"
                >
                  {isUploading ? 'Téléchargement...' : 'Ajouter'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* Stats Modal */}
      <Dialog
        open={isStatsModalOpen}
        onClose={() => setIsStatsModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className={`fixed inset-0 ${theme === 'dark' ? 'bg-black' : 'bg-gray-500'} bg-opacity-30`} />
          <div className={`relative ${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl max-w-md w-full mx-4 p-6 border`}>
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsStatsModalOpen(false)}
                className={theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-600'}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
              Ajouter des statistiques
            </h2>

            <form onSubmit={handleAddStats} className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                  Créateur *
                </label>
                <input
                  type="text"
                  value={statsForm.creator}
                  onChange={(e) => setStatsForm({ ...statsForm, creator: e.target.value })}
                  className="w-full border-2 border-black"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Influenceur concerné (optionnel)
                </label>
                <select
                  value={selectedInfluencerId}
                  onChange={(e) => {
                    const id = e.target.value;
                    setSelectedInfluencerId(id);
                    if (id) {
                      const inf = campaign?.influencers.find(i => i.id === id);
                      setSelectedInfluencerName(inf?.fullName || '');
                      // Pré-remplir le champ créateur avec le nom de l'influenceur
                      setStatsForm(prev => ({...prev, creator: inf?.fullName || ''}));
                    } else {
                      setSelectedInfluencerName('');
                    }
                  }}
                  className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg text-gray-900"
                >
                  <option value="">Sélectionner un influenceur</option>
                  {campaign?.influencers.map((influencer) => (
                    <option key={influencer.id} value={influencer.id}>
                      {influencer.fullName}
                    </option>
                  ))}
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Sélectionnez l'influenceur dont vous ajoutez les statistiques.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                    Vues
                  </label>
                  <input
                    type="number"
                    value={statsForm.views}
                    onChange={(e) => setStatsForm({ ...statsForm, views: parseInt(e.target.value) })}
                    className="w-full border-2 border-black"
                    required
                  />
                </div>
                <div>
                  <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                    Likes
                  </label>
                  <input
                    type="number"
                    value={statsForm.likes}
                    onChange={(e) => setStatsForm({ ...statsForm, likes: parseInt(e.target.value) })}
                    className="w-full border-2 border-black"
                    required
                  />
                </div>
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                  Taux d'engagement (%)
                </label>
                <input
                  type="number"
                  step="0.01"
                  value={statsForm.engagementRate}
                  onChange={(e) => setStatsForm({ ...statsForm, engagementRate: parseFloat(e.target.value) })}
                  className="w-full border-2 border-black"
                  required
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                  URL du post
                </label>
                <input
                  type="url"
                  value={statsForm.postUrl}
                  onChange={(e) => setStatsForm({ ...statsForm, postUrl: e.target.value })}
                  className="w-full border-2 border-black"
                  placeholder="https://"
                />
              </div>

              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-1`}>
                  URL de l'image (miniature)
                </label>
                <input
                  type="url"
                  value={statsForm.postThumb}
                  onChange={(e) => setStatsForm({ ...statsForm, postThumb: e.target.value })}
                  className="w-full border-2 border-black"
                  placeholder="https://"
                />
              </div>

              <div className="flex justify-end gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setIsStatsModalOpen(false)}
                  className={`px-4 py-2 text-sm font-medium ${
                    theme === 'dark'
                      ? 'text-gray-300 bg-dark-200 border-dark-300 hover:bg-dark-300'
                      : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                  } border-2 border-black rounded-lg transition-colors`}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 text-sm font-medium text-white bg-accent-blue border-2 border-black rounded-lg hover:bg-accent-blue/90"
                >
                  Ajouter
                </button>
              </div>
            </form>
          </div>
        </div>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className={`fixed inset-0 ${theme === 'dark' ? 'bg-black' : 'bg-gray-500'} bg-opacity-30`} />
          <div className={`relative ${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl max-w-md w-full mx-4 p-6 border`}>
            <div className="mb-6">
              <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
                Confirmer la suppression
              </h3>
              <p className={`mt-2 text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-500'}`}>
                Êtes-vous sûr de vouloir supprimer cet élément ? Cette action est irréversible.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setIsDeleteModalOpen(false)}
                className={`px-4 py-2 text-sm font-medium ${
                  theme === 'dark'
                    ? 'text-gray-300 bg-dark-200 border-dark-300 hover:bg-dark-300'
                    : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
                } border rounded-lg transition-colors`}
              >
                Annuler
              </button>
              <button
                type="button"
                onClick={() => selectedItem && handleDeleteItem(selectedItem.type, selectedItem.id)}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      </Dialog>

      {/* Public Link Modal */}
      <Dialog
        open={isLinkModalOpen}
        onClose={() => setIsLinkModalOpen(false)}
        className="fixed inset-0 z-10 overflow-y-auto"
      >
        <div className="flex items-center justify-center min-h-screen p-4">
          <Dialog.Overlay className={`fixed inset-0 ${theme === 'dark' ? 'bg-black' : 'bg-gray-500'} bg-opacity-30`} />
          <div className={`relative ${theme === 'dark' ? 'bg-dark-50 border-dark-200' : 'bg-white border-gray-200'} rounded-xl max-w-md w-full mx-4 p-6 border`}>
            <div className="absolute top-4 right-4">
              <button
                onClick={() => setIsLinkModalOpen(false)}
                className={theme === 'dark' ? 'text-gray-400 hover:text-gray-300' : 'text-gray-500 hover:text-gray-600'}
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            <h2 className={`text-xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'} mb-6`}>
              Lien de partage
            </h2>

            <div className="space-y-6">
              <div>
                <label className={`block text-sm font-medium ${theme === 'dark' ? 'text-gray-200' : 'text-gray-700'} mb-2`}>
                  Lien public pour la marque
                </label>
                <div className={`flex items-center gap-2 p-3 ${
                  theme === 'dark' ? 'bg-dark-100 border-dark-200' : 'bg-gray-50 border-gray-200'
                } border rounded-lg`}>
                  <input
                    type="text"
                    value={`${window.location.origin}/suivi/${campaign.linkToken}`}
                    readOnly
                    className={`flex-1 bg-transparent border-none focus:ring-0 ${
                      theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
                    }`}
                  />
                  <button
                    onClick={copyPublicLink}
                    className={`p-2 rounded-lg ${
                      theme === 'dark'
                        ? 'bg-dark-200 hover:bg-dark-300 text-gray-300'
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    } transition-colors`}
                  >
                    {copiedLink ? (
                      <Check className="w-5 h-5 text-green-500" />
                    ) : (
                      <Copy className="w-5 h-5" />
                    )}
                  </button>
                </div>
              </div>

              <div className="flex items-center justify-between">
                <span className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                  Statut du lien
                </span>
                <button
                  onClick={toggleLinkStatus}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full ${
                    campaign.linkActive
                      ? 'bg-accent-blue'
                      : theme === 'dark' ? 'bg-dark-200' : 'bg-gray-200'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition ${
                      campaign.linkActive ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>

              <div className={`p-4 rounded-lg ${
                theme === 'dark' ? 'bg-dark-100' : 'bg-gray-50'
              }`}>
                <div className="flex items-start gap-2">
                  <Info className={`w-5 h-5 flex-shrink-0 ${
                    theme === 'dark' ? 'text-accent-blue' : 'text-indigo-600'
                  }`} />
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-300' : 'text-gray-700'}`}>
                    <p>Ce lien permet à la marque de suivre l'avancement de la campagne sans avoir besoin de se connecter.</p>
                    <p className="mt-1">Seuls les documents marqués comme "Visible pour la marque" seront accessibles.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
    </div>
  );
}